import { useMemo } from "react";

const normalizeName = value => (value || "").toLowerCase().trim();

const buildPatientIdentity = patientInfo => ({
  firstName: patientInfo?.firstName || "",
  lastName: patientInfo?.lastName || "",
});

export const getPatientNameMismatch = (patientData, uploadedFiles) => {
  console.log("[PatientNameMismatch] evaluating", {
    patientData,
    uploadedFilesCount: uploadedFiles?.length || 0,
    uploadedFiles: uploadedFiles?.map(file => ({
      name: file?.name,
      patientInfo: file?.patientInfo || null,
    })),
  });

  if (!patientData || !uploadedFiles || uploadedFiles.length === 0) {
    console.log("[PatientNameMismatch] skipped: missing patientData or files");
    return {
      hasNameMismatch: false,
      mismatchSubject: null,
    };
  }

  const patientFirst = normalizeName(patientData.first_name);
  const patientLast = normalizeName(patientData.last_name);

  const mismatchedFile = uploadedFiles.find(file => {
    if (!file.patientInfo) return false;

    const metaFirst = normalizeName(file.patientInfo.firstName);
    const metaLast = normalizeName(file.patientInfo.lastName);

    // Skip files whose metadata carries no name at all
    if (!metaFirst && !metaLast) return false;

    // Only compare a field if BOTH sides are non-empty
    const firstMismatch = metaFirst && patientFirst && metaFirst !== patientFirst;
    const lastMismatch = metaLast && patientLast && metaLast !== patientLast;

    return firstMismatch || lastMismatch;
  });

  if (!mismatchedFile) {
    console.log("[PatientNameMismatch] no mismatch detected", {
      patientFirst,
      patientLast,
    });
    return {
      hasNameMismatch: false,
      mismatchSubject: null,
    };
  }

  console.log("[PatientNameMismatch] mismatch detected", {
    patientFirst,
    patientLast,
    mismatchedFileName: mismatchedFile?.name,
    mismatchSubject: buildPatientIdentity(mismatchedFile.patientInfo),
  });

  return {
    hasNameMismatch: true,
    mismatchSubject: buildPatientIdentity(mismatchedFile.patientInfo),
  };
};

/**
 * Pure comparison helper — exported for unit testing.
 *
 * Returns true if any file in uploadedFiles has patientInfo whose
 * firstName or lastName (when non-empty) does not match the current
 * patient record (case-insensitive exact match).
 *
 * @param {object|null} patientData  - Current patient: { first_name, last_name }
 * @param {object[]}    uploadedFiles - Processed file objects (may have .patientInfo)
 * @returns {boolean}
 */
export const checkPatientNameMismatch = (patientData, uploadedFiles) =>
  getPatientNameMismatch(patientData, uploadedFiles).hasNameMismatch;

/**
 * Hook that wraps checkPatientNameMismatch in useMemo.
 *
 * @param {object|null} patientData
 * @param {object[]}    uploadedFiles
 * @returns {{ hasNameMismatch: boolean }}
 */
export const usePatientNameMismatch = (patientData, uploadedFiles) => {
  const mismatchInfo = useMemo(
    () => getPatientNameMismatch(patientData, uploadedFiles),
    [patientData, uploadedFiles]
  );
  return mismatchInfo;
};
